import { BILLING_LINK_BATCH_LIMIT, type BillingUnit } from '@smart-link/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Link2,
  Loader2,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useToast } from './Toaster';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Skeleton } from './ui/skeleton';

interface LinkOption {
  id: string;
  name: string;
  domain: string;
  billingUnitId: string | null;
  billingUnitName: string | null;
}

const PAGE_SIZE = 40;

export function BillingLinksDialog({ unit, onClose }: { unit: BillingUnit; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const options = useQuery({
    queryKey: ['billing-link-options'],
    queryFn: () => api<{ rows: LinkOption[] }>('/api/links'),
    refetchOnWindowFocus: false,
  });
  const [links, setLinks] = useState<LinkOption[] | null>(null);
  const [changes, setChanges] = useState<Map<string, boolean>>(() => new Map());
  const [search, setSearch] = useState('');
  const [onlyLinked, setOnlyLinked] = useState(false);
  const [page, setPage] = useState(1);

  // Keep the ownership snapshot until an explicit refresh; background fetches
  // must not reset a selection or silently change its optimistic precondition.
  useEffect(() => {
    if (links === null && options.isSuccess && !options.isFetching) setLinks(options.data.rows);
  }, [links, options.data, options.isSuccess, options.isFetching]);

  const filtered = useMemo(() => {
    const normalize = (value: string) => value.normalize('NFKC').toLowerCase();
    const query = normalize(search.trim());
    return (links ?? []).filter((link) => {
      // Retain pending removals in this view until save so they can be undone.
      if (onlyLinked && link.billingUnitId !== unit.id) return false;
      return normalize(
        `${link.name} ${link.id} ${link.domain} ${link.billingUnitName ?? ''}`,
      ).includes(query);
    });
  }, [links, search, onlyLinked, unit.id]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const isSelected = (link: LinkOption) => changes.get(link.id) ?? link.billingUnitId === unit.id;
  const selectedCount = (links ?? []).filter(isSelected).length;
  const pageSelected = visible.filter(isSelected).length;
  const overLimit = changes.size > BILLING_LINK_BATCH_LIMIT;

  function select(items: LinkOption[], checked: boolean) {
    setChanges((previous) => {
      const next = new Map(previous);
      for (const link of items) {
        if (checked === (link.billingUnitId === unit.id)) next.delete(link.id);
        else next.set(link.id, checked);
      }
      return next;
    });
  }

  const save = useMutation({
    mutationFn: () =>
      api(`/api/billing/units/${unit.id}/links`, {
        method: 'PUT',
        body: JSON.stringify({
          changes: (links ?? [])
            .filter((link) => changes.has(link.id))
            .map((link) => ({
              linkId: link.id,
              previousUnitId: link.billingUnitId,
              linked: changes.get(link.id),
            })),
        }),
      }),
    onSuccess: () => {
      for (const key of [
        'billing-usage',
        'billing-links',
        'billing-units',
        'billing-link-options',
        'links',
        'link',
      ]) {
        qc.invalidateQueries({ queryKey: [key] });
      }
      toast.success('已保存');
      onClose();
    },
  });

  async function refresh() {
    const result = await options.refetch();
    if (result.isSuccess) {
      setLinks(result.data.rows);
      setChanges(new Map());
      setPage(1);
      save.reset();
    }
  }

  const locked = save.isPending || options.isFetching;
  const error = save.error ?? options.error;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) onClose();
      }}
    >
      <DialogContent
        className="max-w-xl gap-0 p-0"
        aria-describedby={undefined}
        onInteractOutside={(event) => {
          if (save.isPending) event.preventDefault();
        }}
      >
        <DialogHeader className="min-w-0 p-5 pr-12">
          <DialogTitle className="truncate" title={unit.name}>
            关联链接 · {unit.name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 border-y px-5 py-3">
          <div className="relative min-w-0 flex-1">
            <Search className="text-muted-foreground absolute top-2.5 left-3 size-4" />
            <Input
              value={search}
              maxLength={500}
              disabled={save.isPending}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="搜索链接"
              aria-label="搜索链接"
              className="pr-8 pl-9"
            />
            {search && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-0.5 right-0.5 size-8"
                title="清空搜索"
                aria-label="清空链接搜索"
                disabled={save.isPending}
                onClick={() => {
                  setSearch('');
                  setPage(1);
                }}
              >
                <X className="size-3.5" />
              </Button>
            )}
          </div>
          <Button
            variant={onlyLinked ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={onlyLinked}
            disabled={save.isPending}
            onClick={() => {
              setOnlyLinked((value) => !value);
              setPage(1);
            }}
          >
            <Link2 />
            只看已关联
          </Button>
          <Button
            variant="ghost"
            size="icon"
            title="刷新"
            aria-label="刷新链接列表"
            disabled={locked}
            onClick={refresh}
          >
            <RefreshCw className={options.isFetching ? 'animate-spin' : ''} />
          </Button>
        </div>
        <div className="flex items-center justify-between gap-3 border-b px-5 py-2 text-xs">
          <div className="flex items-center gap-3">
            <Checkbox
              id="billing-links-page"
              aria-label="选择本页链接"
              checked={
                pageSelected === 0
                  ? false
                  : pageSelected === visible.length
                    ? true
                    : 'indeterminate'
              }
              disabled={locked || visible.length === 0}
              onCheckedChange={(checked) => select(visible, checked === true)}
            />
            <Label htmlFor="billing-links-page" className="text-muted-foreground text-xs">
              本页 {visible.length}
            </Label>
          </div>
          <span className="text-muted-foreground tabular-nums">已选 {selectedCount}</span>
        </div>
        <div className="max-h-[min(24rem,45dvh)] min-h-40 overflow-y-auto px-5">
          {links === null && (options.isPending || options.isFetching) ? (
            <div className="space-y-5 py-5">
              {[1, 2, 3].map((key) => (
                <Skeleton key={key} className="h-9 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-muted-foreground py-12 text-center text-sm">
              {options.isError
                ? '加载失败'
                : search
                  ? '无匹配链接'
                  : onlyLinked
                    ? '暂无关联'
                    : '暂无链接'}
            </div>
          ) : (
            visible.map((link) => {
              const selected = isSelected(link);
              const otherOwner = link.billingUnitId && link.billingUnitId !== unit.id;
              return (
                <div key={link.id} className="flex items-center gap-3 border-b py-3 last:border-0">
                  <Checkbox
                    id={`billing-link-${link.id}`}
                    checked={selected}
                    disabled={locked}
                    aria-label={`选择${link.name}`}
                    onCheckedChange={(checked) => select([link], checked === true)}
                  />
                  <Label
                    htmlFor={`billing-link-${link.id}`}
                    className="min-w-0 flex-1 cursor-pointer flex-col items-start gap-1"
                  >
                    <span
                      className="block max-w-full truncate text-sm font-medium"
                      title={link.name}
                    >
                      {link.name}
                    </span>
                    <span className="text-muted-foreground block font-mono text-xs">
                      /{link.id}
                    </span>
                  </Label>
                  {otherOwner && (
                    <Badge
                      variant="outline"
                      className="max-w-48 gap-1 text-[10px]"
                      title={
                        selected
                          ? `${link.billingUnitName} → ${unit.name}`
                          : (link.billingUnitName ?? '其他客户')
                      }
                    >
                      <span className="truncate">{link.billingUnitName ?? '其他客户'}</span>
                      {selected && (
                        <>
                          <ArrowRight className="size-3 shrink-0" />
                          <span className="truncate">{unit.name}</span>
                        </>
                      )}
                    </Badge>
                  )}
                </div>
              );
            })
          )}
        </div>
        {error && (
          <p role="alert" className="text-destructive px-5 py-2 text-sm">
            {error instanceof Error ? error.message : '保存失败'}
          </p>
        )}
        {overLimit && (
          <p role="alert" className="text-destructive px-5 py-2 text-xs">
            单次最多更改 {BILLING_LINK_BATCH_LIMIT} 条
          </p>
        )}
        <DialogFooter className="flex-wrap items-center justify-between border-t p-4">
          <div className="text-muted-foreground flex items-center gap-2 text-xs tabular-nums">
            <span>{changes.size} 项更改</span>
            {pages > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label="上一页链接"
                  disabled={locked || currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  <ChevronLeft />
                </Button>
                <span>
                  {currentPage} / {pages}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label="下一页链接"
                  disabled={locked || currentPage >= pages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  <ChevronRight />
                </Button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" disabled={save.isPending} onClick={onClose}>
              取消
            </Button>
            <Button
              disabled={locked || changes.size === 0 || overLimit}
              onClick={() => save.mutate()}
            >
              {save.isPending && <Loader2 className="animate-spin" />}保存
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
